/**
 * IXD Preview 비교 결과 렌더 뷰.
 *
 * 스트리밍으로 도착한 TOC 를 CompareSidebar + 본문(PreviewDiffSection)으로 그린다.
 *
 * 라이브 페이지와 다운로드 standalone 뷰어가 **이 컴포넌트를 공유**한다(드리프트 없음):
 * - 다운로드 버튼은 headerAction 으로 주입(뷰어는 미주입). 다운로더를 직접 import 하지
 *   않아 뷰어 번들과 순환 없이 재사용된다.
 * - 사이드바 로고는 logoSrc 로 주입(뷰어는 오프라인용 data URI).
 */

import { useMemo, useRef, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";
import { useScrollSpy } from "@/hooks/use-scroll-spy";
import {
  CompareSidebar,
  type CompareSidebarSection,
} from "@/components/compare/compare-sidebar";
import {
  buildSidebarCategories,
  isConsolidated,
  type SidebarCategoryWithMeta,
} from "@/components/ixd-ixd-compare/preview-diff/build-sidebar-categories";
import { SegmentControl } from "@/toggle-ui-kit/segment-control";
import { NumberBadge } from "@/toggle-ui-kit/badge";
import {
  PreviewDiffSection,
  type PreviewDiffViewMode,
} from "@/components/ixd-ixd-compare/preview-diff/preview-diff-section";
import { countChangedItems } from "@/components/ixd-ixd-compare/preview-diff/preview-diff-changes";
import { useIxdPreviewCompareStore } from "@/stores/ixd-preview-compare";
import { SidebarInset, SidebarProvider } from "@/styles/ui/sidebar";

const escapeId = (id: string) =>
  window.CSS && CSS.escape ? CSS.escape(id) : id;

/** DSD CompareHeader 의 세그먼트 탭과 동일한 스타일. */
const SegmentTab = ({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "relative h-8 inline-flex items-center gap-1 px-2.5 rounded-small overflow-hidden label-1-normal-medium whitespace-nowrap",
      active
        ? "bg-primary-base text-static-white"
        : "border border-line-normal text-label-normal",
    )}
  >
    {label}
    <div
      className={cn(
        "absolute rounded-small pointer-events-none transition-opacity",
        "opacity-0 hover:opacity-6 active:opacity-12",
        active ? "inset-0 bg-static-white" : "-inset-px bg-decorate-neutral",
      )}
    />
  </button>
);

/**
 * 대사한 두 소스의 이름을 보여주는 헤더 바. IXD-DSD / DSD-DSD 대사와 동일한 디자인
 * (NumberBadge 1/2 + label-2-semibold).
 */
const CompareTitleBar = ({
  srcLabel,
  trgLabel,
}: {
  srcLabel: string | null;
  trgLabel: string | null;
}) => (
  <div className="flex flex-row h-13 w-full justify-center items-center p-2 gap-6 border-b border-line-normal bg-background-gray-normal">
    <div className="flex flex-1 min-w-0 justify-center gap-1">
      <NumberBadge className="bg-label-normal text-static-white box-border p-1.5 size-5 caption-2-medium shrink-0">
        1
      </NumberBadge>
      <div className="flex justify-center items-center label-2-semibold truncate min-w-0">
        {srcLabel ?? "기준 IXD"}
      </div>
    </div>
    <div className="flex flex-1 min-w-0 justify-center gap-1">
      <NumberBadge className="bg-label-normal text-static-white box-border p-1.5 size-5 caption-2-medium shrink-0">
        2
      </NumberBadge>
      <div className="flex justify-center items-center label-2-semibold truncate min-w-0">
        {trgLabel ?? "비교 IXD"}
      </div>
    </div>
  </div>
);

const CONSOLIDATION_ITEMS = [
  { value: "연결" as const, label: "연결" },
  { value: "별도" as const, label: "별도" },
];

/**
 * activeSection 구독을 사이드바로 격리한다. 스크롤로 활성 목차가 바뀔 때
 * 콘텐츠(섹션/테이블/셀)가 아니라 이 컴포넌트만 리렌더되도록.
 */
const PreviewCompareSidebar = ({
  categories,
  onSectionClick,
  hideUnchanged,
  logoSrc,
  showConsolidation,
  consolidationFilter,
  setConsolidationFilter,
}: {
  categories: SidebarCategoryWithMeta[];
  onSectionClick: (section: CompareSidebarSection) => void;
  hideUnchanged: boolean;
  logoSrc?: string;
  showConsolidation: boolean;
  consolidationFilter: "연결" | "별도";
  setConsolidationFilter: (value: "연결" | "별도") => void;
}) => {
  const activeSection = useIxdPreviewCompareStore((s) => s.activeSection);

  const wantConsolidated = consolidationFilter === "연결";
  const filteredCategories = showConsolidation
    ? categories.filter((c) => c.consolidated === wantConsolidated)
    : categories;

  const chipSelector = showConsolidation ? (
    <div className="px-3 py-2 border-b border-line-normal">
      <SegmentControl
        items={CONSOLIDATION_ITEMS}
        value={consolidationFilter}
        onChange={setConsolidationFilter}
        size="small"
      />
    </div>
  ) : undefined;

  return (
    <CompareSidebar
      categories={filteredCategories}
      activeSection={activeSection}
      onSectionClick={onSectionClick}
      hideUnchanged={hideUnchanged}
      logoSrc={logoSrc}
      topContent={chipSelector}
    />
  );
};

interface IxdPreviewCompareResultViewProps {
  /** 헤더 우측 슬롯(라이브: 다운로드 버튼 / 뷰어: 없음). */
  headerAction?: ReactNode;
  /** 사이드바 로고 src 오버라이드(뷰어 오프라인용 data URI). */
  logoSrc?: string;
}

export const IxdPreviewCompareResultView = ({
  headerAction,
  logoSrc,
}: IxdPreviewCompareResultViewProps = {}) => {
  const tocs = useIxdPreviewCompareStore((s) => s.tocs);
  const srcLabel = useIxdPreviewCompareStore((s) => s.srcLabel);
  const trgLabel = useIxdPreviewCompareStore((s) => s.trgLabel);
  const setActiveSection = useIxdPreviewCompareStore((s) => s.setActiveSection);
  const setSelectedCellId = useIxdPreviewCompareStore(
    (s) => s.setSelectedCellId,
  );

  const [mode, setMode] = useState<PreviewDiffViewMode>("all");
  const [consolidationFilter, setConsolidationFilter] = useState<
    "연결" | "별도"
  >("연결");
  const changedCount = useMemo(() => countChangedItems(tocs), [tocs]);

  // 항목은 unmount 하지 않고 mode 로 숨긴다. 사이드바는 항상 전체 TOC 를 보여주고
  // 변경 점(파란 점)으로 변경 여부를 표시한다.
  const { categories, showConsolidation } = useMemo(
    () => buildSidebarCategories(tocs),
    [tocs],
  );

  const wantConsolidated = consolidationFilter === "연결";
  const filteredTocs = showConsolidation
    ? tocs.filter(
        (toc) => isConsolidated(toc.displayLabel) === wantConsolidated,
      )
    : tocs;

  const scrollRef = useRef<HTMLDivElement>(null);

  // 본문에 실제로 렌더되는 섹션(필터 적용) 순서. 스크롤 스파이의 활성 판정 기준.
  const orderedSectionIds = useMemo(
    () => filteredTocs.map((toc) => toc.tocId),
    [filteredTocs],
  );

  useScrollSpy({
    scrollRef,
    orderedIds: orderedSectionIds,
    onActiveChange: setActiveSection,
    getElement: (id, root) =>
      root.querySelector(`section[data-section-id="${escapeId(id)}"]`),
  });

  const handleSectionClick = (section: CompareSidebarSection) => {
    const el = scrollRef.current?.querySelector(
      `section[data-section-id="${escapeId(section.id)}"]`,
    );
    el?.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  // 연결/별도 전환 시 본문은 다른 문서로 바뀐다. 맨 위로 리셋해 lazy 마운트가
  // 항상 위에서부터 시작하도록 한다(위로 스크롤할 때 미마운트 카드가 늦게 커지며
  // 생기는 위치 점프 방지).
  const handleConsolidationChange = (value: "연결" | "별도") => {
    setConsolidationFilter(value);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  return (
    <SidebarProvider
      className="h-screen"
      style={
        {
          "--sidebar-width": "192px",
          "--sidebar-width-icon": "3rem",
          minHeight: 0,
        } as React.CSSProperties
      }
    >
      <PreviewCompareSidebar
        categories={categories}
        onSectionClick={handleSectionClick}
        hideUnchanged={mode === "changes"}
        logoSrc={logoSrc}
        showConsolidation={showConsolidation}
        consolidationFilter={consolidationFilter}
        setConsolidationFilter={handleConsolidationChange}
      />

      <SidebarInset className="flex flex-col min-w-0">
        <header className="relative shrink-0 z-20">
          <div className="flex items-center justify-between bg-background-gray-normal border-b border-line-normal">
            <div className="flex items-center gap-2 px-5 py-3">
              <SegmentTab
                active={mode === "all"}
                label="전체"
                onClick={() => setMode("all")}
              />
              <SegmentTab
                active={mode === "changes"}
                label={`변경 사항 (${changedCount})`}
                onClick={() => setMode("changes")}
              />
            </div>
            {headerAction && (
              <div className="flex items-center gap-2 px-4 py-3">
                {headerAction}
              </div>
            )}
          </div>
          <CompareTitleBar srcLabel={srcLabel} trgLabel={trgLabel} />
        </header>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto bg-background-neutral-alternative pb-80"
          onClick={() => setSelectedCellId(null)}
        >
          <div className="flex flex-col gap-5 px-5 py-5">
            {filteredTocs.map((toc) => (
              <PreviewDiffSection key={toc.tocId} toc={toc} mode={mode} />
            ))}
            {tocs.length === 0 && (
              <div className="flex items-center justify-center py-20">
                <p className="body-1-normal-medium text-label-assistive">
                  비교 결과가 없습니다.
                </p>
              </div>
            )}
            {tocs.length > 0 && mode === "changes" && changedCount === 0 && (
              <div className="flex items-center justify-center py-20">
                <p className="body-1-normal-medium text-label-assistive">
                  변경 사항이 없습니다.
                </p>
              </div>
            )}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};
